import { Button, Modal, Space } from 'antd';
import { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchHistoryNode, useSearchStore } from '../store/useSearchStore';
import { FilterBadgeList } from './FilterBadgeList';
import { KeyboardHintsFooter } from './KeyboardHintsFooter';
import SearchInput, { SearchInputHandle } from './SearchInput';

import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { addRecentlySearchedQuery } from '../api/searchHistoryApi';
import { buildSearchRouteQuery, formatSearchQueryTitle } from '../domain/queryCodec';
import { useSearchComposer } from '../hooks/useSearchComposer';
import '../styles/SearchModal.less';

function useHistoryBlock(isActive: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!isActive) return;

    globalThis.history.pushState({ __searchModalOpen: true }, '');

    const handlePopstate = (e: PopStateEvent) => {
      if (!e.state?.__searchModalOpen) {
        onClose();
      }
    };

    globalThis.addEventListener('popstate', handlePopstate);

    return () => {
      globalThis.removeEventListener('popstate', handlePopstate);

      if (globalThis.history.state?.__searchModalOpen) {
        globalThis.history.back();
      }
    };
  }, [isActive, onClose]);
}

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

const SearchModal: FC<SearchModalProps> = ({ open, onClose }) => {
  const hideSearch = useSearchStore(s => s.hideSearch);
  const resetSearch = useSearchStore(s => s.resetSearch);
  const setQueryString = useSearchStore(s => s.setQueryString);
  const availableTags = useSearchStore(s => s.availableTags);
  const availableTagsCategories = useSearchStore(s => s.availableTagsCategories);
  const availableArchitecture = useSearchStore(s => s.availableArchitecture);
  const reloadHistoryData = useSearchStore(s => s.reloadHistoryData);
  const historyData = useSearchStore(s => s.historyData);
  const setHistoryData = useSearchStore(s => s.setHistoryData);
  const navigate = useNavigate();
  useHistoryBlock(open, onClose);
  const historyDataRef = useRef(historyData);
  historyDataRef.current = historyData;
  const searchInputRef = useRef<SearchInputHandle>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const searchBtnRef = useRef<HTMLButtonElement>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);

  const {
    filters,
    freeText,
    draft,
    dropdowns,
    focusedBadgeIndex,
    focusTarget,
    isDisabledSearchButton,
    startDraft,
    updateDraft,
    commitDraft,
    cancelDraft,
    setFreeText,
    promoteFreeTextToSearchFilter,
    setDropdown,
    removeFilter,
    replaceFilter,
    clearAll,
    resetComposer,
    loadFromQuery,
    buildSubmissionSnapshot,
    setFocusedBadgeIndex,
    setFocusTarget,
  } = useSearchComposer({
    availableArchitecture,
    availableTagsCategories,
  });

  useEffect(() => {
    if (!open) return;

    const handler = (event: Event) => {
      const keyboardEvent = event as globalThis.KeyboardEvent;
      if (keyboardEvent.key === 'ArrowLeft' || keyboardEvent.key === 'ArrowRight') {
        const target = event.target as HTMLElement;
        if (target.closest('.ant-modal-wrap')) {
          event.stopPropagation();
        }
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleEscapeCapture = (event: Event) => {
      const keyboardEvent = event as globalThis.KeyboardEvent;
      if (keyboardEvent.key !== 'Escape') return;

      const active = document.activeElement;
      // If focus is on the search input row or an open select dropdown, let
      // SearchInput.handleEscape handle it (it will close the modal when state is clean)
      if (active?.closest('.xm-search-input__row') || active?.closest('.ant-select-dropdown')) {
        return;
      }

      // Focus is elsewhere (badges, footer buttons, prefix buttons, or even body)
      // — redirect it to the search input so a second ESC can close the modal
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      setFocusedBadgeIndex(null);
      requestAnimationFrame(() => searchInputRef.current?.focus());
    };

    document.addEventListener('keydown', handleEscapeCapture, true);
    return () => document.removeEventListener('keydown', handleEscapeCapture, true);
  }, [open, setFocusedBadgeIndex, searchInputRef]);

  useEffect(() => {
    if (!open) return;

    setIsLoadingSearch(false);
    resetComposer();
    const timeoutId = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(timeoutId);
  }, [open, resetComposer]);

  const handleSubmitSearch = useCallback(() => {
    if (isLoadingSearch) return;

    const { queryString } = buildSubmissionSnapshot();
    setIsLoadingSearch(true);

    if (!queryString) {
      setIsLoadingSearch(false);
      onClose();
      resetComposer();
      navigate('/search', { replace: true });
      return;
    }

    setQueryString(queryString);
    onClose();
    resetComposer();

    // Optimistic update: prepend new item immediately so the list is ready on arrival
    const routeQuery = buildSearchRouteQuery(queryString);
    const optimisticItem: SearchHistoryNode = {
      key: routeQuery,
      path: routeQuery,
      title: formatSearchQueryTitle(queryString, availableArchitecture),
      titleText: queryString,
      isLeaf: true,
      date: new Date(),
    };
    setHistoryData([
      optimisticItem,
      ...historyDataRef.current.filter((item) => item.titleText !== queryString),
    ]);

    navigate(`/search?${queryString}`, { replace: true });

    if (GLOB.userInfo?.user_id) {
      void addRecentlySearchedQuery(GLOB.userInfo.user_id, queryString)
        .then(() => reloadHistoryData())
        .catch((error) => {
          Log.error('Failed to save search history:', error);
        });
    }
  }, [
    availableArchitecture,
    buildSubmissionSnapshot,
    isLoadingSearch,
    navigate,
    onClose,
    reloadHistoryData,
    resetComposer,
    setHistoryData,
    setQueryString,
  ]);

  const handleCancel = useCallback(() => {
    resetSearch();
    hideSearch();
    onClose();
    setIsLoadingSearch(false);
    resetComposer();
  }, [hideSearch, onClose, resetComposer, resetSearch]);

  const handleBadgeRemove = useCallback((index: number) => {
    const nextFilters = removeFilter(index);
    if (nextFilters.length > 0) {
      setFocusedBadgeIndex(Math.min(index, nextFilters.length - 1));
    } else {
      setFocusedBadgeIndex(null);
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [removeFilter, setFocusedBadgeIndex]);

  const handleBadgeSave = useCallback((index: number, updatedFilter: typeof filters[number]) => {
    replaceFilter(index, updatedFilter);
    setFocusedBadgeIndex(index);
  }, [replaceFilter, setFocusedBadgeIndex]);

  const handleClearAll = useCallback(() => {
    clearAll();
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [clearAll]);

  const handleBadgeKeyDown = useCallback((event: KeyboardEvent<Element>, index: number) => {
    const forceFocusBadge = (targetIndex: number) => {
      if (focusedBadgeIndex === targetIndex) {
        setFocusedBadgeIndex(null);
        setTimeout(() => setFocusedBadgeIndex(targetIndex), 0);
      } else {
        setFocusedBadgeIndex(targetIndex);
      }
    };

    const movePrevious = () => {
      if (index > 0) {
        setFocusTarget('remove');
        forceFocusBadge(index - 1);
      } else {
        setFocusedBadgeIndex(null);
        requestAnimationFrame(() => searchInputRef.current?.focusSuffixEnd());
      }
    };

    const moveNext = () => {
      if (index < filters.length) {
        setFocusTarget('editor');
        forceFocusBadge(index + 1);
      } else {
        setFocusedBadgeIndex(null);
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    };

    switch (event.key) {
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          movePrevious();
        } else {
          moveNext();
        }
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          setFocusedBadgeIndex(null);
          setFreeText(event.key);
          requestAnimationFrame(() => searchInputRef.current?.focus());
        }
        break;
    }
  }, [
    filters.length,
    focusedBadgeIndex,
    setFocusTarget,
    setFocusedBadgeIndex,
    setFreeText,
  ]);

  const handleClearAllKeyDown = useCallback((event: KeyboardEvent<Element>) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleClearAll();
        break;
      case 'Tab':
        event.preventDefault();
        if (!event.shiftKey) {
          setFocusedBadgeIndex(null);
          requestAnimationFrame(() => cancelBtnRef.current?.focus());
        } else {
          setFocusTarget('remove');
          setFocusedBadgeIndex(filters.length - 1);
        }
        break;
      default:
        break;
    }
  }, [filters.length, handleClearAll, setFocusTarget, setFocusedBadgeIndex]);

  const handleTabFromInput = useCallback((direction: 'forward' | 'backward') => {
    if (direction !== 'backward') return;

    if (!isDisabledSearchButton) {
      searchBtnRef.current?.focus();
    } else {
      cancelBtnRef.current?.focus();
    }
  }, [isDisabledSearchButton]);

  return (
    <Modal
      className='xm-search-modal'
      open={open}
      onCancel={handleCancel}
      destroyOnHidden
      width={650}
      closable={false}
      keyboard={false}
      footer={
        <div className='xm-search-modal-footer'>
          <KeyboardHintsFooter />
          <Space>
            <Button
              ref={cancelBtnRef}
              onMouseDown={(event) => {
                event.preventDefault();
                if (!isLoadingSearch) {
                  handleCancel();
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (!isLoadingSearch) {
                    handleCancel();
                  }
                } else if (event.key === 'Tab' && !event.shiftKey) {
                  event.preventDefault();
                  if (isDisabledSearchButton) {
                    searchInputRef.current?.focusPrefixStart();
                  } else {
                    searchBtnRef.current?.focus();
                  }
                } else if (event.key === 'Tab' && event.shiftKey) {
                  event.preventDefault();
                  if (filters.length > 0) {
                    setFocusTarget('remove');
                    setFocusedBadgeIndex(filters.length);
                  } else {
                    searchInputRef.current?.focusSuffixEnd();
                  }
                }
              }}
              disabled={isLoadingSearch}
            >
              Cancel
            </Button>
            <Button
              ref={searchBtnRef}
              type='primary'
              onClick={() => {
                if (!isLoadingSearch && !isDisabledSearchButton) {
                  handleSubmitSearch();
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!isLoadingSearch && !isDisabledSearchButton) {
                    handleSubmitSearch();
                  }
                } else if (event.key === 'Tab' && !event.shiftKey) {
                  event.preventDefault();
                  searchInputRef.current?.focusPrefixStart();
                } else if (event.key === 'Tab' && event.shiftKey) {
                  event.preventDefault();
                  cancelBtnRef.current?.focus();
                }
              }}
              loading={isLoadingSearch}
              disabled={isDisabledSearchButton}
            >
              Search
            </Button>
          </Space>
        </div>
      }
    >
      <div className='xm-search-modal-input-container'>
        <Space.Compact style={{ width: '100%' }}>
          <SearchInput
            ref={searchInputRef}
            filters={filters}
            freeText={freeText}
            draft={draft}
            dropdowns={dropdowns}
            onFreeTextChange={setFreeText}
            onStartDraft={startDraft}
            onUpdateDraft={updateDraft}
            onCommitDraft={commitDraft}
            onCancelDraft={cancelDraft}
            onPromoteFreeTextToSearchFilter={promoteFreeTextToSearchFilter}
            onSetDropdown={setDropdown}
            onLoadFromQuery={loadFromQuery}
            onSubmitSearch={handleSubmitSearch}
            onClose={handleCancel}
            onTabOut={handleTabFromInput}
            placeholder='Enter text to search or add filters...'
            availableTags={availableTags}
            availableTagsCategories={availableTagsCategories}
            availableArchitecture={availableArchitecture}
          />
        </Space.Compact>
      </div>
      <FilterBadgeList
        filters={filters}
        onRemove={handleBadgeRemove}
        onSave={handleBadgeSave}
        onSubmit={handleSubmitSearch}
        onClearAll={handleClearAll}
        focusedBadgeIndex={focusedBadgeIndex}
        focusTarget={focusTarget}
        onBadgeKeyDown={handleBadgeKeyDown}
        onClearAllKeyDown={handleClearAllKeyDown}
        availableTags={availableTags}
        availableTagsCategories={availableTagsCategories}
        availableArchitecture={availableArchitecture}
      />
    </Modal>
  );
};

export default SearchModal;
